import { EmployeeName } from "@src/components/employee_name";
import { FrameworkComponents } from "@src/types/aggrid_types";
import { ChangelogTableItem } from "@src/types/resource_plan_types";
import {
  AllocationRowData,
  WorkloadTableRowData,
} from "@src/types/role_request_types";
import {
  columnTypes,
  groupHeaderStyleOptions,
  paginationGridOptions,
  preventPinnedColumnJumpingGridOption,
} from "@src/utils/aggrid_utils";
import { getAllocationColumnDefs } from "@src/utils/allocation_column_def_utils";
import {
  CellClassParams,
  ColDef,
  GetDetailRowDataParams,
  GridOptions,
  ICellRendererParams,
  IDetailCellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-enterprise";
import { CustomCellRendererProps } from "ag-grid-react";
import dayjs from "dayjs";
import _ from "lodash";

import {
  ALLOCATION_FIELD,
  CHANGE_TYPE,
  FIELD_TYPE,
} from "./changelog_constants";
import { ChangeTypeRenderer } from "../custom_renderer/change_type_renderer";

/**
 * Builds the grid options for the changelog
 *
 * @returns grid options for the changelog table
 */
export function getChangelogGridOptions(): GridOptions {
  return {
    ...paginationGridOptions,
    domLayout: "autoHeight",
    suppressContextMenu: true,
    tooltipShowMode: "whenTruncated",
    components: getComponents(),
    columnDefs: getColumnsDefs(),
    defaultColDef: {
      ...columnTypes.default,
      resizable: false,
      flex: 1,
    },
  };
}

/**
 * Builds the components / renderers for the changelog master table
 *
 * @returns components for the changelog master table
 */
function getComponents(): FrameworkComponents {
  return {
    nameAvatarRenderer: (props: CustomCellRendererProps) => {
      return <EmployeeName name={props.value} />;
    },
    changeTypeRenderer: (props: CustomCellRendererProps) => {
      return <ChangeTypeRenderer props={props} />;
    },
  };
}

/**
 * Builds the column definitions for the changelog master table
 *
 * @returns column definitions for the changelog master table
 */
function getColumnsDefs(): ColDef[] {
  return [
    {
      headerName: "",
      minWidth: 40,
      maxWidth: 40,
      cellRendererSelector: (props: ICellRendererParams) =>
        props.data.fieldChange.fieldName === ALLOCATION_FIELD && {
          component: "agGroupCellRenderer",
        },
    },
    {
      field: "fieldChange.creationTime",
      headerName: "Updated Date",
      valueFormatter: (params: ValueFormatterParams): string =>
        dayjs(params.value).format("DD/MM/YYYY"),
      filterValueGetter: (params: ValueGetterParams): string =>
        dayjs(params.data.fieldChange?.creationTime).format("DD/MM/YYYY"),
    },
    {
      field: "fieldChange.modifiedBy",
      headerName: "Updated By",
      cellRenderer: "nameAvatarRenderer",
    },
    {
      field: "fieldChange.type",
      headerName: "Type",
      cellRenderer: "changeTypeRenderer",
      valueGetter: (params: ValueGetterParams) =>
        CHANGE_TYPE[params.data.fieldChange?.type],
    },
    {
      field: "projectRoleRequestId",
      headerName: "Request ID",
    },
    {
      field: "fieldChange.fieldName",
      headerName: "Field",
      valueGetter: (params: ValueGetterParams) => {
        const fieldName: string = params.data.fieldChange?.fieldName;
        const formattedName: string = FIELD_TYPE[fieldName];
        return formattedName ?? _.startCase(fieldName);
      },
    },
    {
      field: "fieldChange.oldValue",
      tooltipField: "fieldChange.oldValue",
      headerName: "Old Value",
      flex: 2,
      cellClass: "ellipsis",
    },
    {
      field: "fieldChange.newValue",
      tooltipField: "fieldChange.newValue",
      headerName: "New Value",
      flex: 2,
      cellClass: "ellipsis",
    },
  ];
}

/**
 * Builds the grid cell renderer params for the changelog details table (allocation table).
 *
 * Since every details table has a different number of columns, the column definitions need to be build first
 * and added to the details grid options depending on the data.
 *
 * @param params ag grid cell renderer params
 * @returns the cell renderer params for the changelog details table (allocation table)
 */
export function getDetailCellRendererParams(
  params: ICellRendererParams
): IDetailCellRendererParams {
  let detailColumnDefs: ColDef[] = [];

  const masterData: ChangelogTableItem = params.node.data;

  if (
    masterData.fieldChange.fieldName === ALLOCATION_FIELD &&
    masterData.fieldChange.allocations
  ) {
    const { startDate, endDate } =
      calculateDatesForDetailsColumnDefs(masterData);
    detailColumnDefs = getDetailsColumnDefs(startDate, endDate);
  }
  return {
    detailGridOptions: {
      ...preventPinnedColumnJumpingGridOption,
      ...groupHeaderStyleOptions,
      rowHeight: 40,
      tooltipShowDelay: 0,
      domLayout: "autoHeight",
      suppressContextMenu: true,
      suppressRowHoverHighlight: true,
      components: getComponents(),
      defaultColDef: {
        ...columnTypes.default,
        resizable: false,
        sortable: false,
        cellClass: "no-focus-border",
      },
      columnDefs: detailColumnDefs,
    },
    getDetailRowData: (params: GetDetailRowDataParams) => {
      params.successCallback(params.data?.fieldChange?.allocations);
    },
  } as IDetailCellRendererParams;
}

/**
 * Calculates the first and the last entry from the allocation data on a specific node / row.
 *
 * @param masterNodeData data of the master table on a specific node / row
 * @returns start and end date of the allocation on that specific node / row.
 */
function calculateDatesForDetailsColumnDefs(
  masterNodeData: ChangelogTableItem
): {
  startDate: Date;
  endDate: Date;
} {
  const workloadAllocations: WorkloadTableRowData[] =
    masterNodeData.fieldChange?.allocations;

  const keys: IterableIterator<string> =
    workloadAllocations[0]?.allocations.keys();
  const keysAsArray: string[] = [...keys];

  const startKey: string = keysAsArray[0];
  const endKey: string = _.last(keysAsArray);

  const startDate: Date = dayjs(startKey, "M-YYYY").toDate();
  const endDate: Date = dayjs(endKey, "M-YYYY").toDate();

  return { startDate, endDate };
}

/**
 * Builds the allocation columns for years and months.
 * Starting with the given startDate and ending with the given endDate.
 *
 * @param providedStartDate given start date
 * @param providedEndDate given end date
 * @returns the column definitions for the details table (allocation table)
 */
function getDetailsColumnDefs(
  providedStartDate: Date,
  providedEndDate: Date
): ColDef[] {
  const allocationColumns: ColDef[] = getAllocationColumnDefs(
    providedStartDate,
    providedEndDate,
    getDetailsColDefMonth
  );
  return [
    {
      field: "label",
      headerName: "",
      cellClass: "allocation-label",
      pinned: "left",
      width: 320,
    },
    ...allocationColumns,
  ];
}

/**
 * Builds the column definitions for the monthly allocations.
 * This is basically the columnd definition for the children of the allocation columnd defs.
 *
 * @param year given year (parent column)
 * @param month given month (child column)
 * @returns column definition for the monthly allocation in the details table (allocation table)
 */
function getDetailsColDefMonth(year: number, month: number): ColDef {
  return {
    valueGetter: (params: ValueGetterParams) => {
      const foundAllocation: AllocationRowData = params.data.allocations?.get(
        `${month}-${year}`
      );

      return params.node.rowIndex === 0
        ? foundAllocation.percentage || 0
        : foundAllocation?.updatedPercentage ??
            (foundAllocation?.percentage || 0);
    },
    cellClass: "allocation-cell no-focus-border",
    cellClassRules: {
      "allocation-modified": (params: CellClassParams): boolean => {
        const foundAllocation: AllocationRowData = params.data.allocations?.get(
          `${month}-${year}`
        );

        return (
          params.node.rowIndex === 1 &&
          foundAllocation?.updatedPercentage !== foundAllocation?.percentage
        );
      },
      "allocation-match": (params: CellClassParams): boolean => {
        const foundAllocation: AllocationRowData = params.data.allocations?.get(
          `${month}-${year}`
        );

        return (
          params.node.rowIndex === 1 &&
          foundAllocation?.updatedPercentage === foundAllocation?.percentage
        );
      },
    },
  };
}
